/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// React
import {
	useEffect,
	useLayoutEffect,
	useRef,
	useState,
} from 'react';

// Store
import useStore from '@store/index';

// Types
import {
	TSelectOption,
	TSortColumn,
} from '@global-types/index';

// Hooks
import { ACLS } from '@config/constants/acls';
import { ROLES } from '@config/constants/roles';
import { useAuthorization } from '@lib/authorization';
import { useElementSize } from '@mantine/hooks';
import dayjs from 'dayjs';
import { TParsedSite } from '../types';

/* -------------------------------------------------------------------------- */
/*                                  Hook                                      */
/* -------------------------------------------------------------------------- */

type ObjWithStartDate = {
	startDate: {
		$gte: string;
		$lt: string;
	};
};

/**
 * Custom hook for managing conversion factor list functionality.
 * @returns {Array} An array containing references and callback functions.
 */
export const useSites = () => {
	const [queryParamsProducers, setQueryParamsProducers] =
		useState({ entity: [] });
	const [queryParamsProjects, setQueryParamsProjects] =
		useState({});
	const [resetFilterSites, setResetFilterSites] =
		useState(false);
	const [resetFilterProjects, setResetFilterProjects] =
		useState(false);

	const [isOverflowFilters] = useState(false);

	const [openDrawer, setOpenDrawer] = useState(false);

	const [
		openSelectedItemsToVerifyModal,
		setOpenSelectedItemsToVerifyModal,
	] = useState(false);

	const [
		openSelectedItemsToDownloadModal,
		setOpenSelectedItemsToDownloadModal,
	] = useState(false);

	const [
		openSelectedItemsToDeleteModal,
		setOpenSelectedItemsToDeleteModal,
	] = useState(false);

	const [openEditForm, setOpenEditForm] = useState(false);

	const [showScrollFilters, setShowScrollFilters] =
		useState(false);

	const {
		sitesPage,
		sitesPageSize,
		setSitesPage,
		sitesSelectedRecords,
		setSitesSelectedRecords,
		sitesOpenConfirmationModalDelete,
		setSitesOpenConfirmationModalDelete,
		sitesQueryParams,
		setSitesQueryParams,
		sitesSortStatus,
		setSitesSortStatus,
		setSitesSortParams,
		sitesSortParams,
		setSitesPageSize,
		setSitesSelectedRow,
		sitesSelectedRow,
		setIsOpenStartMint,
		setOpenDrawerSiteForm,
		openDrawerSiteForm,
		sitesDatatableOptions,
		setSitesDatatableOptions,
	} = useStore();

	const containerRef = useRef<HTMLDivElement>(null);

	const handleScroll = (scrollTo: string) => {
		if (containerRef.current) {
			if (scrollTo === 'right') {
				containerRef.current.scrollLeft =
					containerRef.current.scrollLeft +
					containerRef.current.offsetWidth;
			} else {
				containerRef.current.scrollLeft =
					containerRef.current.scrollLeft -
					containerRef.current.offsetWidth;
			}
		}
	};

	const handleSortParams = (_column: TSortColumn) => {
		let sortParams = _column.columnAccessor;
		switch (_column.columnAccessor) {
			case 'place':
				sortParams = 'location.place';
				break;
			case 'mintStatus':
				sortParams = 'mintStatus';
				break;
			default:
				// eslint-disable-next-line @typescript-eslint/no-unused-expressions
				sortParams;
				break;
		}

		if (_column.direction == 'asc') {
			setSitesSortParams(sortParams);
		} else {
			setSitesSortParams('-' + sortParams);
		}
	};

	const handleStartMint = () => {
		//setSitesStartMintsActiveStep(0);
		setIsOpenStartMint(true);
	};

	const handleReset = () => {
		setSitesPageSize(100);
		setSitesQueryParams({});
		setSitesPage(1);
		setSitesSortParams('-createdAt');
		setSitesSortStatus({});
	};

	useEffect(() => {
		return () => {
			handleReset();
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	function hasStartDate(obj: ObjWithStartDate) {
		if (
			Object.keys(obj).length === 0 &&
			obj.constructor === Object
		) {
			return false;
		}

		if (
			obj.startDate &&
			obj.startDate.$gte !== undefined &&
			obj.startDate.$lt !== undefined
		) {
			return true;
		}

		if (
			obj.startDate &&
			obj.startDate.$gte === undefined &&
			obj.startDate.$lt === undefined
		) {
			return false;
		}

		return false;
	}

	const NBR_SELECTED_ROWS = sitesSelectedRecords.length;

	const selectedFields = [
		'_id',
		'project.name',
		'site.name',
		'startDate',
		'quantity',
		'verified',
	];

	const { ref, width: widthContainer } = useElementSize();
	const { checkAcl, checkAccess } = useAuthorization();
	useLayoutEffect(() => {
		if (
			containerRef.current &&
			containerRef.current.clientWidth <
				containerRef.current.scrollWidth
		) {
			setShowScrollFilters(true);
		} else {
			setShowScrollFilters(false);
		}
	}, [widthContainer]);

	/**************************** Pagination ****************************/

	const handlePaginationChangeSize = function (
		_size: string | number
	) {
		setSitesPageSize(Number(_size));
		setSitesPage(1);
	};

	const handleChangePagination = (
		data: number | string
	) => {
		setSitesPage(data);
	};

	/**************************** Filter Projects ****************************/

	const handleapplyprojectfilter = (
		projects: TSelectOption[]
	) => {
		setSitesSelectedRecords([]);
		setSitesPage(1);
		const queryParamsProjects = {
			project: projects.map(
				(_project: TSelectOption) => _project.value
			),
			site: [],
		};
		setQueryParamsProjects(queryParamsProjects);
		setSitesQueryParams({
			...sitesQueryParams,
			...queryParamsProjects,
		});
		setResetFilterSites(true);
	};

	/**************************** Filter Date ****************************/
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const handleapplydatefilter = (date: any) => {
		setSitesSelectedRecords([]);
		let startDate;
		let endDate;
		if (
			dayjs(date[0].value[0]).isValid() &&
			dayjs(date[0].value[1]).isValid()
		) {
			startDate = date[0].value[0];
			endDate = date[0].value[1];
		} else {
			startDate = dayjs()
				.subtract(364, 'days')
				.toISOString();
			endDate = dayjs().toISOString();
		}

		const queryParamsDate = {
			startDate: {
				$gte: startDate,
				$lt: endDate,
			},
		};

		setSitesQueryParams({
			...sitesQueryParams,
			...queryParamsDate,
		});
		setSitesPage(1);
	};
	/**************************** Filter Sites ****************************/

	const handleapplysitefilter = (
		sites: TSelectOption[]
	) => {
		setSitesSelectedRecords([]);
		setSitesPage(1);
		const queryParamsSites = {
			site: sites.map(
				(_site: TSelectOption) => _site.value
			),
		};

		setSitesQueryParams({
			...sitesQueryParams,
			...queryParamsSites,
		});
	};

	/**************************** Filter Producers ****************************/

	const handleapplyproducerfilter = (
		producers: TSelectOption[]
	) => {
		setSitesSelectedRecords([]);
		setSitesPage(1);
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		const queryParamsProducers: any = {
			entity: producers.map(
				(_producer: TSelectOption) => _producer.value
			),
			site: [],
			projects: [],
		};
		setQueryParamsProducers(queryParamsProducers);
		setSitesQueryParams({
			...sitesQueryParams,
			...queryParamsProducers,
		});
		setResetFilterProjects(true);
		setResetFilterSites(true);
	};
	/**************************** Sort ****************************/

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const handlechangesortstatus = (data: any) => {
		handleSortParams(data);
		setSitesSortStatus(data);
	};

	const handleselectedrecordchange = (
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		selectedRecords: any
	) => {
		setSitesSelectedRecords(selectedRecords);
	};

	/**************************** Datatable ****************************/

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const handlerowclicked = (row: any) => {
		if (
			checkAcl(ACLS.SITES.READ) &&
			checkAccess({
				allowedRoles: [
					ROLES.ADMIN,
					ROLES.SUPERADMIN,
					ROLES.STAFF,
				],
			})
		) {
			setSitesSelectedRow(row.record);
			setOpenDrawer(true);
		}
	};

	const handleDeleteMultipleItems = () => {
		setOpenSelectedItemsToDeleteModal(true);
	};

	const handleResetSelectedItems = () => {
		setSitesSelectedRecords([]);
	};

	/**************************** Show more ****************************/

	const handlerowviewmore = (row: TParsedSite) => {
		setSitesSelectedRow(row);
		setOpenDrawer(true);
	};

	const handleCloseDrawerShowMoreSite = () => {
		setOpenDrawer(false);
	};

	/**************************** Edit item ****************************/
	const handleeditsite = (row: TParsedSite) => {
		setSitesSelectedRow(row);
		setOpenEditForm(true);
	};

	const handlecloseeditsite = () =>
		setOpenEditForm(false);
	/**************************** Delete confirmation modal ****************************/

	const handlerowremoveitem = (row: TParsedSite) => {
		setSitesOpenConfirmationModalDelete({
			open: true,
			params: row,
		});
	};

	const handledeleteconfirmationmodalconfirm = (
		remove: any
	) => {
		setSitesOpenConfirmationModalDelete({
			open: false,
			params: {},
		});

		remove.mutate(
			String(sitesOpenConfirmationModalDelete.params.id)
		);
		setOpenDrawer(false);
	};

	const handleclosedeleteconfirmationmodal = () => {
		setSitesOpenConfirmationModalDelete({
			open: false,
			params: {},
		});
	};
	/*************************************** Delete multiple Items  ***************************************/

	const handleConfirmDeleteMultipleItem = (
		removeMultiple: any
	) => {
		const idsList = [...sitesSelectedRecords].map(
			(_apiToken) => _apiToken.id
		);
		removeMultiple.mutate({
			ids: idsList,
		});

		setSitesSelectedRecords([]);
		setOpenSelectedItemsToDeleteModal(false);
	};

	/**************************** Delete confirmation modal ****************************/

	const handledeleteconversionfactor = () => {
		setSitesOpenConfirmationModalDelete({
			open: true,
			params: sitesSelectedRow,
		});
	};

	/**************************** Drawer add conversion factor ****************************/

	const handleCloseDrawerAddSite = () => {
		setOpenDrawerSiteForm(false);
	};

	/********************************************************************************************/

	return {
		/**************************** Drawer add conversion factor ****************************/
		handleCloseDrawerAddSite,
		/**************************** Delete item ****************************/
		handlerowremoveitem,
		handledeleteconfirmationmodalconfirm,
		handleclosedeleteconfirmationmodal,
		handledeleteconversionfactor,
		/**************************** Show more ****************************/
		handlerowviewmore,
		handleCloseDrawerShowMoreSite,
		/**************************** Datatable ****************************/
		handlerowclicked,
		/**************************** Select item ****************************/
		handleselectedrecordchange,
		/**************************** Sort ****************************/
		handlechangesortstatus,
		/**************************** Filters ****************************/
		handleapplyprojectfilter,
		handleapplysitefilter,
		handleapplydatefilter,
		handleapplyproducerfilter,
		/**************************** Pagination ****************************/
		handleChangePagination,
		handlePaginationChangeSize,
		sitesPage,
		sitesPageSize,

		setSitesPage,
		sitesSelectedRecords,
		setSitesSelectedRecords,
		sitesOpenConfirmationModalDelete,
		setSitesOpenConfirmationModalDelete,
		sitesQueryParams,
		setSitesQueryParams,
		sitesSortStatus,
		setSitesSortStatus,
		setSitesSortParams,
		sitesSortParams,
		setSitesPageSize,
		setSitesSelectedRow,
		sitesSelectedRow,
		setIsOpenStartMint,
		queryParamsProducers,
		setQueryParamsProducers,
		queryParamsProjects,
		setQueryParamsProjects,
		handleScroll,
		resetFilterSites,
		setResetFilterSites,
		isOverflowFilters,
		openDrawer,
		setOpenDrawer,
		openSelectedItemsToDownloadModal,
		setOpenSelectedItemsToDownloadModal,
		openSelectedItemsToVerifyModal,
		setOpenSelectedItemsToVerifyModal,
		openSelectedItemsToDeleteModal,
		setOpenSelectedItemsToDeleteModal,
		showScrollFilters,
		setShowScrollFilters,
		openDrawerSiteForm,
		setOpenDrawerSiteForm,
		handleSortParams,
		handleStartMint,
		hasStartDate,
		NBR_SELECTED_ROWS,
		selectedFields,
		ref,
		containerRef,
		resetFilterProjects,
		setResetFilterProjects,
		handleDeleteMultipleItems,
		handleResetSelectedItems,
		handleConfirmDeleteMultipleItem,
		sitesDatatableOptions,
		setSitesDatatableOptions,
		handleeditsite,
		openEditForm,
		setOpenEditForm,
		handlecloseeditsite,
	};
};
